import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Lock, Check, X } from 'lucide-react'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { InputPassword } from '@/components/ui/input-password'
import { Text } from '@/components/ui/text'
import { BackToLoginLink } from '@/components/auth/BackToLoginLink'
import { AuthenticationLayout } from '@/components/layout/AuthenticationLayout.tsx'
import { cn } from '@/lib/utils'

type NewPasswordFormData = {
  password: string
  confirmPassword: string
}

interface PasswordRequirement {
  label: string
  validator: (password: string) => boolean
}

interface NewPasswordFormProps {
  onSubmit: (data: NewPasswordFormData) => Promise<void>
  isLoading?: boolean
  error?: string
}

export function NewPasswordForm({
  onSubmit,
  isLoading = false,
  error,
}: NewPasswordFormProps) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')

  const passwordSchema = z
    .string()
    .min(1, t('validation.passwordRequired'))
    .min(8, t('auth.passwordNotMatchRules'))
    .regex(/[A-Z]/, t('auth.passwordNotMatchRules'))
    .regex(/[0-9]/, t('auth.passwordNotMatchRules'))
    .regex(/[!@#$%^&*()]/, t('auth.passwordNotMatchRules'))

  const newPasswordSchema = z
    .object({
      password: passwordSchema,
      confirmPassword: z
        .string()
        .min(1, t('validation.confirmPasswordRequired')),
    })
    .refine(data => data.password === data.confirmPassword, {
      message: t('auth.passwordMismatch'),
      path: ['confirmPassword'],
    })

  const requirements: PasswordRequirement[] = [
    {
      label: t('auth.passwordRequirement8Chars'),
      validator: pwd => pwd.length >= 8,
    },
    {
      label: t('auth.passwordRequirementUppercase'),
      validator: pwd => /[A-Z]/.test(pwd),
    },
    {
      label: t('auth.passwordRequirementNumber'),
      validator: pwd => /[0-9]/.test(pwd),
    },
    {
      label: t('auth.passwordRequirementSpecial'),
      validator: pwd => /[!@#$%^&*()]/.test(pwd),
    },
  ]

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    trigger,
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (isSubmitted) {
      setTimeout(() => trigger('confirmPassword'), 0)
    }
  }

  return (
    <AuthenticationLayout
      icon={<Lock className='w-6 h-6 text-dark-600' />}
      title={t('auth.setNewPassword')}
      description={t('auth.setNewPasswordDescription')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        {error && (
          <div className='p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md'>
            {error}
          </div>
        )}

        <div className='space-y-2'>
          <Text
            as='label'
            htmlFor='password'
            className='text-sm font-normal'
            variant='formLabel'
          >
            {t('auth.newPassword')}
          </Text>
          <InputPassword
            id='password'
            placeholder={t('auth.enterNewPassword')}
            className={cn(
              'h-11 bg-white',
              errors.password && 'border-red-500',
              !errors.password && 'border-dark-300'
            )}
            {...register('password', {
              onChange: handlePasswordChange,
            })}
            disabled={isLoading}
            autoComplete='password'
          />
          {errors.password && (
            <p className='text-sm text-red-600'>{errors.password.message}</p>
          )}

          <div className='space-y-2 pt-2'>
            {requirements.map((req, index) => {
              const isValid = req.validator(password)
              return (
                <div key={index} className='flex items-center gap-2 text-sm'>
                  {isValid ? (
                    <div className='w-4 h-4 rounded-full bg-green-600 flex items-center justify-center'>
                      <Check className='w-3 h-3 text-white' />
                    </div>
                  ) : (
                    <div className='w-4 h-4 rounded-full bg-dark-300 flex items-center justify-center'>
                      <X className='w-3 h-3 text-white' />
                    </div>
                  )}
                  <span
                    className={cn(
                      'text-sm',
                      isValid ? 'text-green-600' : 'text-dark-600'
                    )}
                  >
                    {req.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div className='space-y-2'>
          <Text
            as='label'
            htmlFor='confirmPassword'
            className='text-sm font-normal'
            variant='formLabel'
          >
            {t('auth.confirmPassword')}
          </Text>
          <InputPassword
            id='confirmPassword'
            placeholder={t('auth.enterConfirmPassword')}
            className={cn(
              'h-11 bg-white',
              errors.confirmPassword && 'border-red-500',
              !errors.confirmPassword && 'border-dark-300'
            )}
            {...register('confirmPassword')}
            disabled={isLoading}
            autoComplete='confirm-password'
          />
          {errors.confirmPassword && (
            <p className='text-sm text-red-600'>
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <Button
          type='submit'
          className='w-full h-11 font-medium'
          disabled={isLoading}
        >
          {t('auth.resetPassword')}
        </Button>

        <BackToLoginLink />
      </form>
    </AuthenticationLayout>
  )
}
